// Bitcoin Navigator shared constants — colors, copy, and per-ticker metadata
// ported from the TradeAlgo-Homepage prototype (nexusMockData.ts / bnPaywall-design.ts).

/** Instrument → allocation-bar/legend color. */
export const NEXUS_TICKER_COLOR = {
  // BTC (long) family — greens
  IBIT: "#4CAF50",
  FBTC: "#43A047",
  ARKB: "#66BB6A",
  BITB: "#2E7D32",
  BITO: "#81C784",
  // Inverse / cash
  BITI: "#F44336",
  CASH: "#CBD5E1",
};

export const NEXUS_TICKER_FALLBACK_COLOR = "#94A3B8";

/** Stance → accent color. */
export const NEXUS_STANCE_COLOR = {
  LONG: "#4CAF50",
  SHORT: "#F44336",
  CASH: "#5B5B5D",
};

/** Stance → headline shown in the Allocation Signal callout. */
export const STANCE_HEADLINE = {
  LONG: "Long Exposure Active",
  SHORT: "Short Hedge Active",
  CASH: "Capital Preservation Mode",
};

/** Tickers the strategy treats as long-BTC exposure (stance derivation). */
export const LONG_BTC_TICKERS = ["IBIT", "FBTC", "ARKB", "BITB", "BITO"];

/** Tickers the strategy treats as inverse/short-BTC exposure. */
export const INVERSE_BTC_TICKERS = ["BITI"];

/**
 * Rebalance reasons arrive as markdown; flatten to plain prose for the
 * one-line contexts (Allocation Signal rationale, history reason strip).
 */
export const stripInlineMarkdown = (text = "") =>
  text
    .replace(/\*\*/g, "")
    .replace(/^\s*[-*]\s+/gm, "")
    .replace(/\s*\n+\s*/g, " ")
    .trim();

export const ENGINE_EXPLAINER =
  "Driven by an ensemble of three independently-trained reinforcement-learning models. Each votes a target across IBIT (long BTC), BITI (inverse BTC), and cash; the votes are averaged into one allocation. Evaluated daily and rebalanced on a 1% drift threshold, trading on roughly two-thirds of days.";

export const BN_DEFAULT_TITLE = "BTC ETF RL Ensemble";

export const BN_DEFAULT_DESCRIPTION =
  "AI-managed Bitcoin exposure through a live reinforcement-learning ensemble.";

export const BN_DISCLAIMER =
  "This website provides AI-powered insights for informational purposes only. Please use at your own discretion, and consult a qualified financial advisor for personalized guidance.";

/**
 * Per-ticker fallback metadata for the crypto-ETF universe. The company-data
 * API has thin coverage for these ETFs, so the drawer's About tab and the
 * rebalancing rows fall back to this curated copy (paragraphs split on \n\n).
 */
export const BN_TICKER_META = {
  IBIT: {
    name: "iShares Bitcoin Trust ETF",
    sector: "Long BTC ETF",
    about:
      "IBIT is the iShares Bitcoin Trust ETF, a spot Bitcoin ETF issued by BlackRock. It holds Bitcoin directly and tracks the spot BTC price with minimal tracking error (under 0.1% on most days). Expense ratio: 0.25%.\n\nIn this strategy, IBIT is the long BTC vehicle. When the model is bullish on BTC price direction, capital is allocated to IBIT. When the model turns bearish or neutral, IBIT is exited and capital moves to BITI or cash.",
  },
  BITI: {
    name: "ProShares Short Bitcoin Strategy ETF",
    sector: "Short BTC ETF",
    about:
      "BITI is the ProShares Short Bitcoin Strategy ETF. It seeks the inverse (-1x) of the daily performance of a Bitcoin futures index, rising when BTC falls. Expense ratio: 0.95%.\n\nIn this strategy, BITI is the hedge and short vehicle. When the model turns bearish on BTC, capital rotates from cash or IBIT into BITI to profit from downside while staying inside a regulated ETF wrapper.",
  },
  FBTC: {
    name: "Fidelity Wise Origin Bitcoin Fund",
    sector: "Long BTC ETF",
    about:
      "FBTC is Fidelity's spot Bitcoin ETF, holding BTC directly with a 0.25% expense ratio.\n\nUsed alongside IBIT as a long BTC vehicle to diversify issuer exposure across the sleeve.",
  },
  ARKB: {
    name: "ARK 21Shares Bitcoin ETF",
    sector: "Long BTC ETF",
    about:
      "ARKB is the ARK 21Shares spot Bitcoin ETF, holding BTC directly.\n\nA secondary long BTC sleeve used to spread issuer and liquidity risk.",
  },
  BITB: {
    name: "Bitwise Bitcoin ETF",
    sector: "Long BTC ETF",
    about:
      "BITB is the Bitwise spot Bitcoin ETF, holding BTC directly at a low expense ratio.\n\nA long BTC sleeve used to diversify issuer exposure.",
  },
  BITO: {
    name: "ProShares Bitcoin Strategy ETF",
    sector: "BTC Futures ETF",
    about:
      "BITO holds CME Bitcoin futures rather than spot BTC.\n\nUsed for tactical long BTC exposure with deep futures-based liquidity.",
  },
  CASH: {
    name: "U.S. Dollar",
    sector: "U.S. Dollar",
    about:
      "Cash is the strategy's neutral, capital-preservation state, held in U.S. dollars.\n\nWhen no model has a high-conviction directional signal, the ensemble parks weight in cash. This lowers exposure between trades and acts as the risk buffer that keeps drawdowns contained.",
  },
};

/** Paywall preview-card palette (differs from the dashboard bar by design). */
export const BNP = {
  ibit: "#E0A34A",
  biti: "#7B93C4",
  cash: "#4A5670",
  cardBorder: "#eaeaed",
  cardDivider: "#f2f2f5",
};

/** Static allocation shown on the paywall's preview card. */
export const BNP_PREVIEW_ALLOCATION = [
  { ticker: "CASH", weight: 66.7, color: BNP.cash },
  { ticker: "BITI", weight: 33.3, color: BNP.biti },
  { ticker: "IBIT", weight: 0.0, color: BNP.ibit },
];

export const BN_PAYWALL_HEADLINE =
  "Turn Bitcoin’s Volatility Into Opportunity.";

export const BN_PAYWALL_BODY =
  "An AI-managed portfolio of spot-Bitcoin ETFs that goes long, hedges, or moves to cash as the market turns, all inside a regulated brokerage account. No wallets, no keys, no self-custody.";

export const BN_PAYWALL_FEATURES = [
  {
    icon: "bothWays",
    title: "Trades both directions",
    body: "Long spot-Bitcoin ETFs in uptrends, an inverse ETF to hedge selloffs, and cash when conviction is low.",
  },
  {
    icon: "shield",
    title: "No wallets, no keys",
    body: "Every position is a U.S.-listed ETF held in your own brokerage account. Nothing touches an exchange.",
  },
  {
    icon: "signal",
    title: "Rationale on every move",
    body: "The AI re-evaluates daily and explains each rebalance in writing, on the platform and by alert.",
  },
];

export const BN_PAYWALL_DISCLAIMER =
  "Bitcoin Navigator provides AI-powered portfolio signals for informational purposes only. Digital-asset ETFs are volatile and past performance is not indicative of future results. Please invest at your own discretion and consult a qualified financial adviser.";
